import React, { useState, useEffect } from 'react';
import CountrySelector from './CountrySelector';
import RegionSearchBox from '../../../components/RegionSearchBox';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import wineRegionService, { WineRegion, PaginatedResponse } from '../../../services/wineRegionService';
import { getCountryName } from '../../../utils/countryUtils';

const RegionList: React.FC = () => {
  const navigate = useNavigate();
  const { regionId } = useParams<{ regionId?: string }>();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [regions, setRegions] = useState<WineRegion[]>([]);
  const [currentRegion, setCurrentRegion] = useState<WineRegion | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<WineRegion[]>([]);
  const [isCountrySelectorOpen, setIsCountrySelectorOpen] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    page: 0, // MUI uses 0-indexed pages
    pageSize: 20,
    totalItems: 0,
    totalPages: 0
  });
  
  // Search state
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Function to fetch regions based on current state
  const fetchRegions = async () => {
    setLoading(true);
    try {
      let response: PaginatedResponse<WineRegion>;
      
      if (!regionId) {
        // Fetch top-level countries
        response = await wineRegionService.getCountries(
          pagination.page + 1, // API uses 1-indexed pages
          pagination.pageSize
        );
        console.log('Countries data:', response.data);
        setRegions(response.data);
        setPagination({
          ...pagination,
          totalItems: response.pagination.totalItems,
          totalPages: response.pagination.totalPages
        });
        setBreadcrumbs([]);
        setCurrentRegion(null);
      } else {
        // Fetch children of the specified region
        response = await wineRegionService.getWineRegionChildren(
          regionId,
          pagination.page + 1, // API uses 1-indexed pages
          pagination.pageSize
        );
        setRegions(response.data);
        setPagination({
          ...pagination,
          totalItems: response.pagination.totalItems,
          totalPages: response.pagination.totalPages
        });
        
        // Fetch the current region details
        const region = await wineRegionService.getWineRegion(regionId);
        console.log('Current region details:', region);
        console.log('Region hierarchyPath:', region.hierarchyPath);
        console.log('Region hierarchyNames:', region.hierarchyNames);
        setCurrentRegion(region);
        
        // Build breadcrumbs based on the region path
        await buildBreadcrumbs(region);
      }
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load regions based on the current regionId or search query
  useEffect(() => {
    // Reset search mode when navigating to a specific region
    if (regionId) {
      setIsSearchMode(false);
      setSearchQuery('');
    }
    
    fetchRegions();
  }, [regionId, pagination.page, pagination.pageSize]);

  // Build breadcrumbs based on the region hierarchy
  const buildBreadcrumbs = async (region: WineRegion) => {
    console.log('Building breadcrumbs for region:', region);
    
    try {
      // Special case for Piedmont in Italy
      if (region.name === 'Piedmont' && region.country === 'IT') {
        console.log('Special case: Piedmont in Italy');
        
        // First try to get Italy
        try {
          const italyRegion = await wineRegionService.getWineRegion('IT');
          console.log('Found Italy region:', italyRegion);
          
          // Set breadcrumbs to Italy > Piedmont
          setBreadcrumbs([italyRegion, region]);
          return;
        } catch (error) {
          console.error('Failed to fetch Italy region:', error);
          // Continue with other methods if this fails
        }
      }
      
      // Try to use hierarchyPath and hierarchyNames if available
      if (region.hierarchyPath && region.hierarchyNames && 
          Array.isArray(region.hierarchyPath) && Array.isArray(region.hierarchyNames) &&
          region.hierarchyPath.length > 0 && region.hierarchyNames.length > 0) {
        
        console.log('Using hierarchyPath and hierarchyNames for breadcrumbs');
        console.log('hierarchyPath:', region.hierarchyPath);
        console.log('hierarchyNames:', region.hierarchyNames);
        
        // Create breadcrumb regions from the hierarchy information
        const breadcrumbsData: WineRegion[] = [];
        
        // Create a region object for each level in the hierarchy
        for (let i = 0; i < region.hierarchyPath.length; i++) {
          const id = region.hierarchyPath[i];
          const name = region.hierarchyNames[i];
          
          breadcrumbsData.push({
            _id: id,
            name: name,
            level: i + 1, // Estimate level based on position
            path: '', // Not needed for breadcrumbs
            createdAt: '',
            updatedAt: ''
          });
        }
        
        console.log('Final breadcrumbs data from hierarchy:', breadcrumbsData);
        setBreadcrumbs(breadcrumbsData);
        return;
      }
      
      // Try to use country and parent information
      if (region.level !== undefined && region.level > 1 && region.country) {
        console.log('Using country information for breadcrumbs');
        
        try {
          // Get the country region
          const countryRegion = await wineRegionService.getWineRegion(region.country);
          console.log('Country region:', countryRegion);
          
          // Set breadcrumbs to Country > Current Region
          setBreadcrumbs([countryRegion, region]);
          return;
        } catch (error) {
          console.error(`Failed to fetch country ${region.country}:`, error);
          // Continue with other methods if this fails
        }
      }
      
      // Fallback: Just use the current region
      console.log('Using only current region for breadcrumbs');
      setBreadcrumbs([region]);
    } catch (error) {
      console.error('Failed to build breadcrumbs:', error);
      setBreadcrumbs([]);
    }
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      ...pagination,
      page: 0,
      pageSize: parseInt(event.target.value, 10)
    });
  };

  // Navigate to region details
  const handleRegionClick = (region: WineRegion) => {
    navigate(`/admin/regions/${region._id}`);
  };

  // Navigate to edit region
  const handleEditRegion = (event: React.MouseEvent, region: WineRegion) => {
    event.stopPropagation();
    navigate(`/admin/regions/${region._id}/edit`);
  };

  // Handle delete region
  const handleDeleteRegion = async (event: React.MouseEvent, region: WineRegion) => {
    event.stopPropagation();
    if (window.confirm(`Are you sure you want to delete ${region.name}?`)) {
      try {
        await wineRegionService.deleteWineRegion(region._id);
        // Refresh the list
        setRegions(regions.filter(r => r._id !== region._id));
      } catch (error) {
        console.error('Failed to delete region:', error);
        alert('Failed to delete region. It may have child regions or be referenced by wines.');
      }
    }
  };

  // Handle country selection from the selector
  const handleCountrySelect = async (countryCode: string, countryName: string) => {
    console.log(`Selected country: ${countryCode} (${countryName})`);
    setIsCountrySelectorOpen(false);
    
    try {
      // Create a new country region
      const newCountry = await wineRegionService.createWineRegion({
        name: countryCode,
        level: 1,
        path: countryCode, // Required field
        country: countryCode
      });
      
      // Refresh the regions list
      if (!currentRegion) {
        // If at root level, add to the current list
        setRegions([...regions, newCountry]);
      } else {
        // If viewing a country, navigate to the root to see all countries
        navigate('/admin/regions');
      }
    } catch (error) {
      console.error('Failed to create country:', error);
      alert(`Failed to create country ${countryName}. Please try again.`);
    }
  };
  
  // Handle search submission
  const handleSearch = async (query: string) => {
    console.log('Searching for:', query);
    setSearchQuery(query);
    setIsSearchMode(true);
    setLoading(true);
    
    try {
      // Reset pagination when performing a new search
      const newPagination = {
        ...pagination,
        page: 0
      };
      setPagination(newPagination);
      
      // Fetch search results
      const response = await wineRegionService.searchWineRegions(
        query,
        1, // API uses 1-indexed pages
        newPagination.pageSize
      );
      
      console.log('Search results:', response);
      
      if (response && Array.isArray(response.data)) {
        setRegions(response.data);
        setPagination({
          ...newPagination,
          totalItems: response.pagination.totalItems,
          totalPages: response.pagination.totalPages
        });
      } else {
        console.error('Invalid search results format:', response);
        setRegions([]);
      }
      
      // Clear current region and breadcrumbs in search mode
      setCurrentRegion(null);
      setBreadcrumbs([]);
    } catch (error) {
      console.error('Error searching wine regions:', error);
      setRegions([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle region selection from search results
  const handleSelectSearchResult = (regionId: string) => {
    console.log('Selected region from search results:', regionId);
    
    if (!regionId) {
      console.warn('Invalid region ID selected');
      return;
    }
    
    // Navigate to the selected region
    navigate(`/admin/regions/${regionId}`);
    
    // Exit search mode
    setIsSearchMode(false);
    setSearchQuery('');
  };
  
  // Clear search and return to normal browsing mode
  const handleClearSearch = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    
    // Reset pagination
    setPagination({
      ...pagination,
      page: 0
    });
    
    // Re-fetch current view
    fetchRegions();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Search Box */}
        <Box sx={{ mb: 2 }}>
          <RegionSearchBox 
            onSearch={handleSearch}
            onSelectRegion={handleSelectSearchResult}
            fullWidth={true}
            size="medium"
          />
        </Box>

        {/* Header with breadcrumbs and actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            {/* Breadcrumbs navigation - only show if not at top level */}
            {currentRegion && (
              <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    component={RouterLink}
                    to="/admin/regions"
                    underline="hover"
                    color="inherit"
                  >
                    All Regions
                  </Link>
                  {breadcrumbs.map((breadcrumb, index) => {
                    // For the last breadcrumb (current page), don't make it a link
                    const isLast = index === breadcrumbs.length - 1;
                    
                    // Check if the breadcrumb name is a country code and convert it
                    const displayName = /^[A-Z]{2}$/.test(breadcrumb.name) 
                      ? getCountryName(breadcrumb.name) 
                      : breadcrumb.name;
                    
                    return isLast ? (
                      <Typography color="text.primary" key={breadcrumb._id}>
                        {displayName}
                      </Typography>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={`/admin/regions/${breadcrumb._id}`}
                        underline="hover"
                        color="inherit"
                        key={breadcrumb._id}
                      >
                        {displayName}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
              </Box>
            )}

            {/* Current Region Information */}
            {currentRegion && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  Current: {/^[A-Z]{2}$/.test(currentRegion.name)
                    ? getCountryName(currentRegion.name)
                    : currentRegion.name}
                </Typography>
                
                {currentRegion.localName && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Local: {currentRegion.localName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              // If we're at the root level or viewing a country, open the country selector
              if (!currentRegion || currentRegion.level === 1) {
                setIsCountrySelectorOpen(true);
              } 
              // Otherwise, create a peer region by using the parent of the current region
              else if (currentRegion.parent) {
                navigate(`/admin/regions/create?parent=${currentRegion.parent}`);
              }
              // Fallback to creating a child of the current region
              else {
                navigate(`/admin/regions/create?parent=${currentRegion._id}`);
              }
            }}
          >
            Add {currentRegion && currentRegion.level === 1 ? 'Country' : 'Region'}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : regions.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3 }}>
            No regions found. Click "Add Region" to create one.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{currentRegion ? 'Subregion Name' : 'Region Name'}</TableCell>
                    <TableCell>Local Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regions.map((region) => (
                    <TableRow 
                      key={region._id}
                      hover
                      onClick={() => handleRegionClick(region)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        } 
                      }}
                    >
                      <TableCell>
                        {/* Always convert to country name if it's a 2-letter code */}
                        {/^[A-Z]{2}$/.test(region.name)
                          ? getCountryName(region.name)
                          : region.name}
                        
                        {/* Show hierarchy path in search results */}
                        {isSearchMode && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {region.hierarchyNames?.join(' > ') || 
                             (region.hierarchyPath && region.hierarchyPath.join(' > ')) || 
                             ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {region.localName || '-'}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {/* Only show edit button for non-country regions (regions with parent) */}
                        {region.hierarchyPath && region.hierarchyPath.length > 1 && (
                          <Tooltip title="Edit">
                            <IconButton 
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditRegion(e, region);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Delete">
                          <IconButton 
                            color="error"
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRegion(e, region);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={pagination.totalItems}
              rowsPerPage={pagination.pageSize}
              page={pagination.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Country Selector Dialog */}
      <CountrySelector 
        open={isCountrySelectorOpen}
        onClose={() => setIsCountrySelectorOpen(false)}
        onSelectCountry={handleCountrySelect}
      />
    </Box>
  );
};

export default RegionList;
